import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, CreditCard, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { paymentApi, type PaymentTokenStripeData } from '@/routes/outil-pro/features/payment/api/paymentApi';
import { CardPaymentForm } from '@/routes/outil-pro/features/payment/components/CardPaymentForm';
import { SepaPaymentForm } from '@/routes/outil-pro/features/payment/components/SepaPaymentForm';
import { SepaSetupForm } from '@/routes/outil-pro/features/payment/components/SepaSetupForm';
import { ConfirmDialog } from '@/Components/shared/ConfirmDialog';
import env from '@/config/env';

interface DirectPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentLink: string;
  onPaymentSuccess?: () => void;
  /** Message affiché en haut du formulaire */
  successMessage?: string;
}

/**
 * Modale autonome pour le paiement Stripe en direct.
 * Résout le token depuis le paymentLink, initialise Stripe Elements
 * et affiche le formulaire adapté (Card / SEPA setup / SEPA payment).
 */
export function DirectPaymentModal({
  isOpen,
  onClose,
  paymentLink,
  onPaymentSuccess,
  successMessage = 'Procédez au paiement ci-dessous.',
}: DirectPaymentModalProps) {
  const [tokenData, setTokenData] = useState<PaymentTokenStripeData | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  // Resolve payment token on open
  useEffect(() => {
    if (!isOpen || tokenData || tokenLoading) return;

    const token = paymentLink.split('/').pop();
    if (!token) {
      setTokenError('Lien de paiement invalide.');
      return;
    }

    setTokenLoading(true);
    paymentApi
      .resolveToken(token)
      .then((data) => {
        if (data.requiresSignature) {
          // Le mode "Paiement en direct" est réservé à la signature manuscrite ;
          // ce cas ne doit jamais se produire, mais on gère par sécurité.
          setTokenError('Ce contrat nécessite une signature électronique avant paiement.');
          setTokenLoading(false);
          return;
        }
        setTokenData(data);
        setTokenLoading(false);
      })
      .catch((err) => {
        const message = err?.response?.data?.message || 'Impossible de charger le formulaire de paiement.';
        setTokenError(message);
        setTokenLoading(false);
      });
  }, [isOpen, paymentLink, tokenData, tokenLoading]);

  const stripePromise = useMemo(() => {
    if (!tokenData) return null;
    const stripePublicKey = env.STRIPE_PUBLIC_KEY;
    if (!stripePublicKey || stripePublicKey === 'false') return null;
    return loadStripe(stripePublicKey, { stripeAccount: tokenData.stripeAccountId });
  }, [tokenData]);

  const handlePaymentSuccess = useCallback(async () => {
    const token = paymentLink.split('/').pop();
    if (token) {
      try {
        await paymentApi.completePayment(token);
      } catch {
        // Token may already be marked
      }
    }
    setPaymentSuccess(true);
    onPaymentSuccess?.();
    // Laisse le bloc vert visible brièvement, puis ferme la modale
    // pour passer la main au toast côté parent (évite le doublon visuel).
    setTimeout(() => onClose(), 1500);
  }, [paymentLink, onPaymentSuccess, onClose]);

  const handleClose = useCallback(() => {
    if (!paymentSuccess && tokenData) {
      setShowCloseWarning(true);
      return;
    }
    onClose();
  }, [paymentSuccess, tokenData, onClose]);

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <CreditCard className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            </div>
            <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
              Paiement en direct
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="tw-p-6">
          <DirectPaymentContent
            tokenLoading={tokenLoading}
            tokenError={tokenError}
            tokenData={tokenData}
            stripePromise={stripePromise}
            paymentSuccess={paymentSuccess}
            onPaymentSuccess={handlePaymentSuccess}
            successMessage={successMessage}
          />
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-p-6 tw-border-t tw-border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Alerte de confirmation si fermeture sans paiement */}
      <ConfirmDialog
        isOpen={showCloseWarning}
        variant="warning"
        title="Paiement non effectué"
        message="Le paiement n'a pas encore été effectué. Êtes-vous sûr de vouloir quitter ?"
        confirmLabel="Continuer le paiement"
        cancelLabel="Quitter sans payer"
        zIndex={110}
        onConfirm={() => setShowCloseWarning(false)}
        onCancel={() => {
          setShowCloseWarning(false);
          onClose();
        }}
      />
    </div>
  );
}

/**
 * Contenu interne du formulaire Stripe Elements
 */
function DirectPaymentContent({
  tokenLoading,
  tokenError,
  tokenData,
  stripePromise,
  paymentSuccess,
  onPaymentSuccess,
  successMessage,
}: {
  tokenLoading: boolean;
  tokenError: string | null;
  tokenData: PaymentTokenStripeData | null;
  stripePromise: ReturnType<typeof loadStripe> | null;
  paymentSuccess: boolean;
  onPaymentSuccess: () => void;
  successMessage: string;
}) {
  if (paymentSuccess) {
    return (
      <div className="tw-text-center tw-space-y-4">
        <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-6">
          <CheckCircle2 className="tw-w-12 tw-h-12 tw-text-green-600 tw-mx-auto tw-mb-3" />
          <p className="tw-text-sm tw-text-green-800 tw-font-medium tw-mb-0">
            Paiement effectué avec succès !
          </p>
        </div>
      </div>
    );
  }

  if (tokenLoading) {
    return (
      <div className="tw-text-center tw-py-8">
        <Loader2 className="tw-w-10 tw-h-10 tw-text-affinicar-primary tw-animate-spin tw-mx-auto tw-mb-3" />
        <p className="tw-text-sm tw-text-gray-600">Chargement du formulaire de paiement...</p>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="tw-text-center tw-py-8">
        <AlertTriangle className="tw-w-10 tw-h-10 tw-text-red-500 tw-mx-auto tw-mb-3" />
        <p className="tw-text-sm tw-text-red-600 tw-font-medium">Erreur</p>
        <p className="tw-text-sm tw-text-gray-600">{tokenError}</p>
      </div>
    );
  }

  if (!tokenData || !stripePromise) {
    return (
      <div className="tw-text-center tw-py-8">
        <AlertTriangle className="tw-w-10 tw-h-10 tw-text-yellow-500 tw-mx-auto tw-mb-3" />
        <p className="tw-text-sm tw-text-gray-600">Le service de paiement n'est pas disponible.</p>
      </div>
    );
  }

  return (
    <div className="tw-space-y-4">
      <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
        <p className="tw-text-sm tw-text-blue-800 tw-font-medium tw-mb-0">
          {successMessage}
        </p>
      </div>

      <Elements
        stripe={stripePromise}
        options={{ clientSecret: tokenData.clientSecret }}
      >
        {tokenData.intentType === 'setup_intent' ? (
          <SepaSetupForm
            clientSecret={tokenData.clientSecret}
            onSuccess={onPaymentSuccess}
            companyName={tokenData.metadata?.companyName}
            monthlyAmount={tokenData.metadata?.monthlyAmount}
            duration={tokenData.metadata?.duration}
          />
        ) : tokenData.paymentMethod === 'sepa_debit' ? (
          <SepaPaymentForm
            clientSecret={tokenData.clientSecret}
            onSuccess={onPaymentSuccess}
            sellingPrice={tokenData.metadata?.sellingPrice}
            companyName={tokenData.metadata?.companyName}
          />
        ) : (
          <CardPaymentForm
            clientSecret={tokenData.clientSecret}
            onSuccess={onPaymentSuccess}
            sellingPrice={tokenData.metadata?.sellingPrice}
            companyName={tokenData.metadata?.companyName}
          />
        )}
      </Elements>
    </div>
  );
}
